// Glow-shared data namespace (window.backlit.data). Backed by the data
// API at /_backlit/data{,/{key}}. Works on Public + Private glows alike —
// the glow JWT cookie is enough.

import { normalizeBody } from "./body.js";
import { BacklitError } from "./errors.js";
import { getBackend, type BlobResult, type ListEntry, type ListOptions, type ListPage, type BatchEntry, type GetBatchOptions, type PutResult, type PutIfMatchResult } from "./backend.js";
import { validateDataKey, validateDataPrefix } from "./validate.js";
import {
  JSON_CONTENT_TYPE,
  encodeJSON,
  decodeJSON,
  decodeBatchJSONPage,
  carryTags,
  runUpdateJSON,
  type JSONResult,
  type BatchJSONEntry,
  type PutJSONIfMatchResult,
  type UpdateJSONOptions,
} from "./json.js";

export interface DataNamespace {
  get(key: string): Promise<BlobResult>;
  put(key: string, body: BodyInit, contentType: string): Promise<PutResult>;
  // putIfMatch is an atomic compare-and-swap: the write lands only if the
  // key's current stored value hashes to expectedCrc32c. An empty
  // expectedCrc32c means "create-only" — write iff the key is absent.
  // A CRC mismatch resolves { matched: false, witness } (the current
  // value); it never throws. Real failures (not_found when a non-empty
  // expected is given for an absent key, forbidden, quota_exceeded, …)
  // throw a BacklitError.
  putIfMatch(key: string, body: BodyInit, contentType: string, expectedCrc32c: string): Promise<PutIfMatchResult>;
  delete(key: string): Promise<void>;
  // list returns one bounded page of entries plus an opaque `next` cursor
  // (DATA-1). Pass opts.cursor (a prior page's `next`) to continue, and
  // opts.limit to request a page size (clamped server-side to [1,1000],
  // default 500). Paginate until next is "". Omitting opts returns the first
  // page at the default size.
  list(prefix?: string, opts?: ListOptions): Promise<ListPage<ListEntry>>;

  // getBatch is the value-bearing sibling of list: the same list-shaped input,
  // the same { items, next } page, but each entry carries its value (a Blob).
  // An entry the server dropped at the per-response value budget arrives
  // metadata-only with omitted:true (+ an open `reason`, e.g. "too_large") —
  // narrow on `.omitted` like putIfMatch's `.matched`. opts.limit is clamped
  // server-side to [1,500] (default 100); paginate on `next` until "".
  getBatch(prefix?: string, opts?: GetBatchOptions): Promise<ListPage<BatchEntry>>;

  // --- JSON convenience (composes get/put/putIfMatch; "application/json") ---

  // getJSON reads key and JSON.parses the body. Resolves the parsed value
  // AND its crc32c so the value drops straight into putJSONIfMatch. Throws
  // not_found on a miss (like get) and invalid_body if the stored bytes
  // aren't JSON.
  getJSON<T = unknown>(key: string): Promise<JSONResult<T>>;
  // getBatchJSON is getBatch with each included value JSON.parsed client-side
  // (content-type-agnostic, getJSON parity). A value that fails to parse
  // degrades that entry to the omitted arm with reason "not_json"; a
  // server-omitted entry passes through with its wire reason. One T per page.
  getBatchJSON<T = unknown>(prefix?: string, opts?: GetBatchOptions): Promise<ListPage<BatchJSONEntry<T>>>;
  // putJSON JSON.stringifies value and stores it as application/json.
  // Throws invalid_argument if value has no JSON form (undefined,
  // circular, BigInt, …).
  putJSON(key: string, value: unknown): Promise<PutResult>;
  // putJSONIfMatch is the JSON form of putIfMatch — the default for any
  // shared key more than one session can write. On a mismatch the witness
  // is already parsed (witness.value is the object, witness.crc32c the crc
  // to retry against); a mismatch never throws.
  putJSONIfMatch<T = unknown>(key: string, value: unknown, expectedCrc32c: string): Promise<PutJSONIfMatchResult<T>>;
  // updateJSON is the safe read-modify-write for shared state: load, run
  // `mutate`, commit with a compare-and-swap, and rebase+retry on a
  // concurrent write so no edit is lost. `mutate(current)` gets the current
  // value (undefined on first write) and returns the new value; it must be
  // replayable (it can run again on conflict). Resolves the committed
  // { value, crc32c }; throws BacklitError(code:"conflict") if it can't win
  // within maxAttempts (default 6).
  updateJSON<T = unknown>(key: string, mutate: (current: T | undefined) => T, options?: UpdateJSONOptions): Promise<JSONResult<T>>;
}

export function createData(): DataNamespace {
  // Assigned to a const so the JSON methods can call the base methods via
  // closure (ns.get/ns.put/ns.putIfMatch) rather than `this` — robust to
  // destructuring (`const { getJSON } = backlit.data`).
  const ns: DataNamespace = {
    get(key: string): Promise<BlobResult> {
      validateDataKey(key);
      return getBackend().dataGet(key);
    },

    put(key: string, body: BodyInit, contentType: string): Promise<PutResult> {
      validateDataKey(key);
      const norm = normalizeBody(body, contentType);
      return getBackend().dataPut(key, norm.body, norm.contentType);
    },

    putIfMatch(
      key: string,
      body: BodyInit,
      contentType: string,
      expectedCrc32c: string,
    ): Promise<PutIfMatchResult> {
      validateDataKey(key);
      if (typeof expectedCrc32c !== "string") {
        throw new BacklitError("invalid_argument", "expectedCrc32c must be a string");
      }
      const norm = normalizeBody(body, contentType);
      return getBackend().dataPutIfMatch(key, norm.body, norm.contentType, expectedCrc32c);
    },

    delete(key: string): Promise<void> {
      validateDataKey(key);
      return getBackend().dataDelete(key);
    },

    list(prefix: string = "", opts?: ListOptions): Promise<ListPage<ListEntry>> {
      validateDataPrefix(prefix);
      return getBackend().dataList(prefix, opts);
    },

    getBatch(prefix: string = "", opts?: GetBatchOptions): Promise<ListPage<BatchEntry>> {
      validateDataPrefix(prefix);
      return getBackend().dataGetBatch(prefix, opts);
    },

    async getJSON<T = unknown>(key: string): Promise<JSONResult<T>> {
      const r = await ns.get(key);
      return carryTags(r, { value: await decodeJSON<T>(r.value, key), crc32c: r.crc32c });
    },

    async getBatchJSON<T = unknown>(prefix: string = "", opts?: GetBatchOptions): Promise<ListPage<BatchJSONEntry<T>>> {
      return decodeBatchJSONPage<T, BatchEntry>(await ns.getBatch(prefix, opts));
    },

    async putJSON(key: string, value: unknown): Promise<PutResult> {
      // async so a non-serializable value rejects (not a sync throw),
      // keeping the JSON trio uniformly Promise-rejecting. encodeJSON
      // runs first so the failure is fast, before any round trip; ns.put
      // then validates the key.
      return ns.put(key, encodeJSON(value), JSON_CONTENT_TYPE);
    },

    async putJSONIfMatch<T = unknown>(
      key: string,
      value: unknown,
      expectedCrc32c: string,
    ): Promise<PutJSONIfMatchResult<T>> {
      const r = await ns.putIfMatch(key, encodeJSON(value), JSON_CONTENT_TYPE, expectedCrc32c);
      if (r.matched) return carryTags(r, { matched: true as const, crc32c: r.crc32c });
      return {
        matched: false,
        witness: carryTags(r.witness, { value: await decodeJSON<T>(r.witness.value, key), crc32c: r.witness.crc32c }),
      };
    },

    updateJSON<T = unknown>(
      key: string,
      mutate: (current: T | undefined) => T,
      options?: UpdateJSONOptions,
    ): Promise<JSONResult<T>> {
      return runUpdateJSON<T>((k) => ns.getJSON<T>(k), (k, v, exp) => ns.putJSONIfMatch<T>(k, v, exp), key, mutate, options);
    },
  };

  return ns;
}
